/**
 * Shared helpers for variable placeholder handling in URLs/hrefs.
 * Variables in links use the format {{.VARIABLE_TYPE}} (e.g., {{.CUSTOMER_FIRST_NAME}})
 */

// Pattern for {{.VARIABLE_TYPE}} format
export const VARIABLE_PATTERN = /\{\{\.([\w_]+)\}\}/gi;
export const URL_ENCODED_VARIABLE_PATTERN = /%7B%7B\.([\w_]+)%7D%7D/gi;

/**
 * Check if a string contains variable placeholders.
 */
export function containsVariables(str) {
  if (!str) return false;
  return VARIABLE_PATTERN.test(str) || URL_ENCODED_VARIABLE_PATTERN.test(str);
}

/**
 * Decode URL-encoded variable placeholders to the readable form {{.VAR}}.
 */
export function decodeVariablePlaceholders(str) {
  if (!str) return str;
  return str.replace(URL_ENCODED_VARIABLE_PATTERN, (match, varType) => `{{.${varType}}}`);
}

/**
 * Replace {{.VARIABLE_TYPE}} with actual variable values.
 * Handles both regular and URL-encoded formats.
 *
 * @param {string} str - input string
 * @param {object} variables - resolved variables object (id -> {text, variableType, ...})
 * @returns {string} string with variables interpolated
 */
export function interpolateVariables(str, variables) {
  if (!str || !variables) return str;

  let result = str;

  // Create a map of variableType -> value for quick lookup
  const typeToValue = {};
  Object.values(variables).forEach(variable => {
    // Use variableType (the actual type like CUSTOMER_FIRST_NAME)
    const varType = variable.variableType || variable.type;
    if (varType && variable.text) {
      typeToValue[varType] = variable.text;
    }
  });

  // Replace URL-encoded version: %7B%7B.TYPE%7D%7D
  result = result.replace(URL_ENCODED_VARIABLE_PATTERN, (match, varType) => {
    const value = typeToValue[varType];
    return value ? encodeURIComponent(value) : match;
  });

  // Replace regular version: {{.TYPE}}
  result = result.replace(VARIABLE_PATTERN, (match, varType) => {
    const value = typeToValue[varType];
    return value !== undefined ? value : match;
  });

  return result;
}
